import {
  Offset,
  OffsetProps,
  PositionHandler,
  PositionParam,
  Positions,
} from './types';

const MAX_WIDTH_DESKTOP = 330;
const TOOLTIP_SPACING = 16;
const TOOLTIP_X_VIEWPORT_SPACING = 24;
const TWO = 2;

const setTooltipPosition = (
  position: PositionParam,
  container: DOMRect,
  tooltip: DOMRect,
  tooltipElement: HTMLSpanElement,
  offset: Offset,
  transform: string
) => {
  tooltipElement.classList.add(position);
  const { style } = tooltipElement;
  const marginYOffset = offset.top - offset.bottom;
  const marginXOffset = /^left/gm.test(position)
    ? offset.right - offset.left
    : offset.left - offset.right;

  let y = 0;
  let x = 0;
  let positionY: 'top' | 'bottom' = 'top';
  let positionX: 'left' | 'right' = 'left';

  if (/^left|right/.test(position)) {
    positionX = /^right/.test(position) ? 'right' : 'left';
    y = marginYOffset + container.height / 2;
    x = -(marginXOffset + Math.floor(tooltip.width) / 2 + TOOLTIP_SPACING);
    style.width = `${tooltip.width}px`;
  }

  if (/^top/.test(position)) {
    y = marginYOffset + (Math.floor(tooltip.height) / 2 + TOOLTIP_SPACING) * -1;
    x = marginXOffset + container.width / 2;
  }

  if (/^bottom/.test(position)) {
    positionY = 'bottom';
    y = -(marginYOffset + Math.floor(tooltip.height) / 2 + TOOLTIP_SPACING);
    x = marginXOffset + container.width / 2;
  }

  style.transform = transform;
  style[positionY] = `${y}px`;
  style[positionX] = `${x}px`;
};

const positionHandler: PositionHandler = {
  top: (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(-50%, -50%)'
    );
  },
  'top-left': (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(-10%, -50%)'
    );
  },
  'top-right': (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(-90%, -50%)'
    );
  },
  right: (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(50%, -50%)'
    );
  },
  'right-top': (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(50%, -20%)'
    );
  },
  'right-bottom': (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(50%, -80%)'
    );
  },
  bottom: (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(-50%, 50%)'
    );
  },
  'bottom-left': (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(-10%, 50%)'
    );
  },
  'bottom-right': (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(-90%, 50%)'
    );
  },
  left: (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(-50%, -50%)'
    );
  },
  'left-top': (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(-50%, -20%)'
    );
  },
  'left-bottom': (position, container, tooltip, tooltipElement, offset) => {
    setTooltipPosition(
      position,
      container,
      tooltip,
      tooltipElement,
      offset,
      'translate(-50%, -80%)'
    );
  },
};

export const handleLeftOverflow = (
  tooltipRect: DOMRect,
  screenWidth: number
) => {
  if (tooltipRect.x < 0) {
    return tooltipRect.width + tooltipRect.x - TOOLTIP_X_VIEWPORT_SPACING;
  }

  if (window.innerWidth > screenWidth) {
    const widthDiff =
      tooltipRect.width + tooltipRect.x - TOOLTIP_X_VIEWPORT_SPACING;
    return widthDiff > MAX_WIDTH_DESKTOP ? MAX_WIDTH_DESKTOP : widthDiff;
  }

  return tooltipRect.width;
};

export const handleRightOverflow = (
  tooltipRect: DOMRect,
  screenWidth: number
) => {
  const rightX = tooltipRect.x + tooltipRect.width + TOOLTIP_X_VIEWPORT_SPACING;

  if (rightX > window.innerWidth) {
    const overflowedPx = rightX - window.innerWidth;
    return tooltipRect.width - overflowedPx - TOOLTIP_X_VIEWPORT_SPACING;
  }

  if (window.innerWidth > screenWidth) {
    const overflowedPx = window.innerWidth - rightX;
    const widthDiff =
      tooltipRect.width + overflowedPx - TOOLTIP_X_VIEWPORT_SPACING;
    return widthDiff > MAX_WIDTH_DESKTOP ? MAX_WIDTH_DESKTOP : widthDiff;
  }

  return tooltipRect.width;
};

export const handlePercentageOffset = (
  childrenElement: HTMLElement,
  containerRect: DOMRect,
  offset: OffsetProps
) => {
  const { width } = containerRect;
  const { marginTop, marginLeft, marginBottom, marginRight } =
    window.getComputedStyle(childrenElement);

  const convertedOffset = Object.entries(offset).reduce(
    (acc, [key, value]: [string, number | string]) => {
      let parsedValue = value;

      if (typeof value === 'string') {
        parsedValue = Math.round(
          width * (parseInt(value.split('%')[0], 10) / 100)
        );
      }

      return { ...acc, [key]: parsedValue };
    },
    {} as Offset
  );

  return {
    top: convertedOffset.top + parseInt(marginTop.split('px')[0], 10) / TWO,
    bottom:
      convertedOffset.bottom + parseInt(marginBottom.split('px')[0], 10) / TWO,
    left: convertedOffset.left + parseInt(marginLeft.split('px')[0], 10) / TWO,
    right:
      convertedOffset.right + parseInt(marginRight.split('px')[0], 10) / TWO,
  } as Offset;
};

export const handlePositioning = (
  position: PositionParam,
  containerRect: DOMRect,
  tooltipRect: DOMRect,
  tooltipElement: HTMLSpanElement,
  offset: Offset
) => {
  Object.values(Positions)
    .filter((pos) => pos !== position)
    .forEach((pos) => {
      if (tooltipElement.classList.contains(pos)) {
        tooltipElement.classList.remove(pos);
      }
    });

  return positionHandler[position](
    position,
    containerRect,
    tooltipRect,
    tooltipElement,
    offset
  );
};
